import { authStorage } from '@/shared/libs/authStorage';
import { useAuthStore } from '@/shared/store/authStore';

export interface IAuthRefreshTokenResponse {
  accessToken: string;
  /**
   * Fin **absolue** de la session, ou `null` si l'organisation n'en fixe aucune
   * (suivora-api#927). Absent d'une API antérieure au champ.
   */
  sessionExpiresAt?: string | null;
}

/**
 * Ce qu'un renouvellement apprend.
 *
 * Le booléen ne suffisait plus : la fin absolue de session ne se lit nulle part ailleurs — elle
 * vit dans un cookie httpOnly, et seule cette réponse la porte.
 */
export type RefreshOutcome = {
  ok: boolean;
  /** `null` = aucun plafond absolu ; `undefined` = on ne sait pas, ne rien décompter sur cet axe. */
  sessionExpiresAt?: string | null;
};

// Promise partagée entre tous les appels concurrents à `tryRefreshToken`.
// Le backend fait de la rotation des refresh tokens (l'ancien est révoqué dès
// qu'un nouveau est émis). Sans déduplication, si plusieurs requêtes reçoivent
// un 401 au même moment et tentent chacune un refresh, la première réussit et
// les autres voient le refresh token révoqué -> déconnexion immédiate.
let inflightRefresh: Promise<RefreshOutcome> | null = null;

/**
 * Attempts to silently refresh the access token using the httpOnly refresh token cookie.
 * - Sends a POST request to `/api/auth/refresh-token` with credentials included.
 * - If the request fails or no access token is returned, clears the session.
 * - If successful, updates the access token both in storage and in the Zustand store.
 * - Concurrent callers share the same in-flight request to avoid double-refresh races
 *   against the backend's refresh-token rotation.
 *
 * @returns l'issue du renouvellement, et la fin absolue de session quand l'API la sert.
 */
export function tryRefreshToken(): Promise<RefreshOutcome> {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    try {
      const res = await fetch('/api/auth/refresh-token', {
        method: 'POST',
        credentials: 'include', // send the httpOnly cookie
      });

      if (!res.ok) {
        useAuthStore.getState().clearSession();
        return { ok: false };
      }

      const data = (await res.json()) as IAuthRefreshTokenResponse;
      if (!data?.accessToken) {
        useAuthStore.getState().clearSession();
        return { ok: false };
      }

      // Keep both the raw localStorage mirror and the Zustand store in sync.
      authStorage.setAccessToken(data.accessToken);
      useAuthStore.getState().setAccessToken(data.accessToken);
      return { ok: true, sessionExpiresAt: data.sessionExpiresAt };
    } catch {
      return { ok: false };
    } finally {
      // Libère le slot dès que la promise est réglée (succès ou échec) pour que
      // les futurs 401 puissent redéclencher un refresh.
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}
